import React, { useState, useEffect } from 'react';
import {
  Award,
  Users,
  Bell,
  Shield,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Calendar,
  FileText,
  CheckSquare,
  TrendingUp,
  Hash,
  BarChart3,
  MoreHorizontal,
  ArrowLeft,
  GraduationCap,
  Languages,
  Moon,
  Sun,
  X,
  Phone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useAdmin } from '../../hooks/useAdmin';
import { useOwner } from '../../hooks/useOwner';
import { useAuth } from '../../hooks/useAuth';
import { useInstructor } from '../../hooks/useInstructor';
import { useInterpreter } from '../../hooks/useInterpreter';
import { useTheme } from '../../hooks/useTheme';
import type { AppView } from '../../types/views';
import {
  supabaseReminderStorage,
  supabaseAdminTasksStorage,
} from '../../services/supabaseStorage';
import { countUrgentProcessNumberSessions } from '../../services/processNumbersService';
import { SystemSettingsService } from '../../services/systemSettingsService';
import VoiceToDoWidget from '../VoiceToDoWidget';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from '../admin/NotificationBell';
import { Customer, Booking, Instructor } from '../../types/crm';
import { QuoteWithDetails } from '../../types/quotes';
import { setWorkspaceOverride } from '../../utils/workspacePreference';
import { SaveMobileNumberModal } from '../admin/SaveMobileNumberModal';

interface MobileNavigationProps {
  activeView: AppView;
  onViewChange: (view: AppView, context?: any) => void;
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  canGoBack?: boolean;
  onGoBack?: () => void;
  customers?: Customer[];
  bookings?: Booking[];
  instructors?: Instructor[];
  quotes?: QuoteWithDetails[];
}

const PRIMARY_TABS: Array<{ id: string; label: string; view: AppView; icon: typeof LayoutDashboard }> = [
  { id: 'dashboard', label: 'Home', view: 'dashboard', icon: LayoutDashboard },
  { id: 'clients', label: 'Clients', view: 'clients', icon: Users },
  { id: 'bookings', label: 'Bookings', view: 'bookings', icon: Calendar },
  { id: 'quotes', label: 'Quotes', view: 'quotes', icon: FileText },
];

export function MobileNavigation({
  activeView,
  onViewChange,
  isOpen,
  onToggle,
  canGoBack,
  onGoBack,
  customers = [],
  bookings = [],
  instructors = [],
  quotes = [],
}: MobileNavigationProps) {
  const [expiringCount, setExpiringCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [processNumbersAlertCount, setProcessNumbersAlertCount] = useState(0);
  const [companyName, setCompanyName] = useState('Rapid Cert');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('/images/logo.png');
  const [showMobileNumberModal, setShowMobileNumberModal] = useState(false);
  const { isSuperAdmin } = useSuperAdmin();
  const { isAdmin } = useAdmin();
  const { isOwner } = useOwner();
  const { user, signOut } = useAuth();
  const { instructor } = useInstructor();
  const { interpreter } = useInterpreter();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const canSaveMobileForMfa = isAdmin || isSuperAdmin;

  const handleSignOut = async () => {
    await signOut();
    navigate('/thank-you');
  };

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [allReminders, pendingTasks, urgentProcessSessions] = await Promise.all([
          supabaseReminderStorage.getReminders(),
          supabaseAdminTasksStorage.getPendingTasksCount(),
          countUrgentProcessNumberSessions(),
        ]);

        const today = new Date().toISOString().split('T')[0];
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        const activeRemindersCount = allReminders.filter((r) => {
          if (r.isRenewed) return false;
          if (r.expiryDate === 'Invalid Date') return false;

          let expiryDate: Date;
          if (r.expiryDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            expiryDate = new Date(r.expiryDate);
            if (isNaN(expiryDate.getTime())) return false;
          } else {
            const parts = r.expiryDate.split('-');
            if (parts.length !== 3) return false;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            expiryDate = new Date(year, month, day);
            if (isNaN(expiryDate.getTime())) return false;
          }

          const todayDate = new Date(today);
          return expiryDate >= todayDate && expiryDate <= thirtyDaysFromNow;
        }).length;

        setExpiringCount(activeRemindersCount);
        setPendingTasksCount(pendingTasks);
        setProcessNumbersAlertCount(urgentProcessSessions);
      } catch (error) {
        console.error('Error loading notification counts:', error);
      }
    };

    loadCounts();
  }, []);

  useEffect(() => {
    const loadCompanyBranding = async () => {
      try {
        const branding = await SystemSettingsService.getCompanyBranding();
        if (branding.company_name) setCompanyName(branding.company_name);
        if (branding.company_logo_url) setCompanyLogoUrl(branding.company_logo_url);
      } catch {
        // keep defaults
      }
    };
    loadCompanyBranding();
  }, []);

  const moreItems = [
    {
      id: 'certificates',
      label: 'Certificates',
      icon: Award,
      view: 'certificates' as const,
      description: 'Generate & Manage',
    },
    {
      id: 'reminders',
      label: 'Reminders',
      icon: Bell,
      view: 'reminders' as const,
      description: 'Certificate Tracking',
      badge: expiringCount,
    },
    {
      id: 'process-numbers',
      label: 'Process Numbers / Safe Pass',
      icon: Hash,
      view: 'process-numbers' as const,
      description: 'Safe Pass process numbers & cards',
      badge: processNumbersAlertCount > 0 ? processNumbersAlertCount : undefined,
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      view: 'tasks' as const,
      description: 'Tasks & Customer Reminders',
      badge: pendingTasksCount,
    },
    ...(isAdmin || isSuperAdmin
      ? [
          {
            id: 'financial',
            label: 'Financial Reports',
            icon: TrendingUp,
            view: 'financial' as const,
            description: 'Revenue & Profit',
          },
        ]
      : []),
    ...(isOwner || isSuperAdmin
      ? [
          {
            id: 'owner-analytics',
            label: 'Efficiency Dashboard',
            icon: BarChart3,
            view: 'owner-analytics' as const,
            description: 'Admin throughput & SLA',
          },
        ]
      : []),
    ...(isAdmin && !isSuperAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            icon: Shield,
            view: 'admin' as const,
            description: 'Administration Panel',
          },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          {
            id: 'superadmin',
            label: 'Super Admin',
            icon: Shield,
            view: 'superadmin' as const,
            description: 'System Administration',
          },
        ]
      : []),
    {
      id: 'faq',
      label: 'Help & FAQ',
      icon: HelpCircle,
      view: 'faq' as const,
      description: 'Support & Documentation',
    },
  ];

  const primaryViews = new Set(PRIMARY_TABS.map((t) => t.view));
  const moreIsActive = !primaryViews.has(activeView);

  const handleNavigation = (view: AppView) => {
    onViewChange(view);
    onToggle(false);
  };

  const titleForView = () => {
    const primary = PRIMARY_TABS.find((t) => t.view === activeView);
    if (primary) return primary.label;
    const more = moreItems.find((t) => t.view === activeView);
    return more?.label || companyName;
  };

  return (
    <>
      {/* Compact mobile top bar — fixed so it never participates in page flex width */}
      <div className="fixed inset-x-0 top-0 z-40 border-b border-slate-200/60 bg-white/95 pt-[env(safe-area-inset-top)] backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95">
        <div className="flex w-full items-center gap-2 px-3 py-2.5">
          {canGoBack && onGoBack ? (
            <button
              type="button"
              onClick={onGoBack}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg bg-white shadow-sm">
                <img
                  src={companyLogoUrl}
                  alt=""
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/logo.png';
                  }}
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">{titleForView()}</p>
                <p className="truncate text-[10px] text-slate-400">{companyName}</p>
              </div>
            </div>
          )}

          {canGoBack && onGoBack && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{titleForView()}</p>
            </div>
          )}

          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            <GlobalSearch onViewChange={onViewChange} variant="overlay" />
            <NotificationBell
              customers={customers}
              bookings={bookings}
              instructors={instructors}
              quotes={quotes}
              isSuperAdmin={isSuperAdmin}
              currentUser={user}
              onTasksClick={() => onViewChange('tasks')}
              onSuperAdminTabClick={(tab) => onViewChange('superadmin', { activeTab: tab })}
              onNavigate={(url) => {
                if (url.startsWith('/clients')) onViewChange('clients');
                else if (url.startsWith('/bookings')) onViewChange('bookings');
                else if (url.startsWith('/quotes')) onViewChange('quotes');
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom tab bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200/80 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95"
        aria-label="Primary"
      >
        <div className="grid grid-cols-5">
          {PRIMARY_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeView === tab.view;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleNavigation(tab.view)}
                className={`flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium transition-colors ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'stroke-[2.25]' : ''}`} />
                <span className="truncate">{tab.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => onToggle(!isOpen)}
            className={`flex flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium transition-colors ${
              moreIsActive || isOpen
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
            onClick={() => onToggle(false)}
            aria-hidden
          />
          <div className="fixed bottom-0 left-0 right-0 z-[70] max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white shadow-2xl dark:bg-slate-900 pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">More</h2>
              <button
                type="button"
                onClick={() => onToggle(false)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-1 p-3">
              {moreItems.map((item) => {
                const isActive = activeView === item.view;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavigation(item.view)}
                    className={`flex w-full items-center rounded-xl px-3 py-3 text-left transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                        : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{item.label}</div>
                      <div className="truncate text-xs text-slate-500">{item.description}</div>
                    </div>
                    {'badge' in item && item.badge && item.badge > 0 ? (
                      <span className="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {(instructor || interpreter) && (
              <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-700">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Workspace
                </p>
                <div className="space-y-1">
                  {instructor && (
                    <button
                      type="button"
                      onClick={() => {
                        setWorkspaceOverride('instructor');
                        navigate('/instructor-assignments');
                        onToggle(false);
                      }}
                      className="flex w-full items-center rounded-xl px-3 py-3 text-left text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <GraduationCap className="mr-3 h-5 w-5" />
                      <span className="font-medium">Trainer panel</span>
                    </button>
                  )}
                  {interpreter && (
                    <button
                      type="button"
                      onClick={() => {
                        setWorkspaceOverride('interpreter');
                        navigate('/interpreter-assignments');
                        onToggle(false);
                      }}
                      className="flex w-full items-center rounded-xl px-3 py-3 text-left text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <Languages className="mr-3 h-5 w-5" />
                      <span className="font-medium">Interpreter panel</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 px-3 py-3 dark:border-slate-700">
              <button
                type="button"
                onClick={toggleTheme}
                className="mb-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {theme === 'dark' ? (
                  <Sun className="mr-3 h-5 w-5" />
                ) : (
                  <Moon className="mr-3 h-5 w-5" />
                )}
                <span className="font-medium">
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </span>
              </button>

              {user && (
                <div className="mb-2 px-1 pt-2">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Quick Tasks
                  </p>
                  <VoiceToDoWidget
                    currentUser={{
                      id: user.id,
                      email: user.email || 'unknown@example.com',
                      user_metadata: user.user_metadata,
                    }}
                  />
                </div>
              )}

              {canSaveMobileForMfa && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMobileNumberModal(true);
                    onToggle(false);
                  }}
                  className="mb-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-slate-700 hover:bg-teal-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <Phone className="mr-3 h-5 w-5 text-teal-500" />
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Mobile number</div>
                    <div className="text-xs text-slate-500">For Phone MFA setup</div>
                  </div>
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  handleSignOut();
                  onToggle(false);
                }}
                className="flex w-full items-center rounded-xl px-3 py-3 text-left hover:bg-red-50 dark:hover:bg-red-950/30"
              >
                <LogOut className="mr-3 h-5 w-5 text-red-500" />
                <div>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Sign Out</div>
                  <div className="text-xs text-slate-500">End your session</div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {showMobileNumberModal && (
        <SaveMobileNumberModal onClose={() => setShowMobileNumberModal(false)} />
      )}
    </>
  );
}
